import { NgModule, LOCALE_ID, ErrorHandler } from '@angular/core';
import {
    APP_BASE_HREF, PlatformLocation,
    registerLocaleData
} from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { ContentService } from '@common/services/content.service';
import {
    OAuthModule,
    OAuthStorage,
    ValidationHandler,
    AuthConfig, OAuthModuleConfig,
} from 'angular-oauth2-oidc';
import { JwksValidationHandler } from 'angular-oauth2-oidc-jwks';
import {
    API_URL,
    COMMON_API_URL,
    CMS_ALLOW_LINKS,
    CMS_URL,
    CMS_PRODUCT,
    CMS_REGION,
    CMS_LOCALE,
    BUSINESS,
    CANONICAL_COMMUNITY,
    ANALYTICS_API_URL,
    DEFAULT_COMMUNITY,
    ANALYTICS_LOG_TO_CONSOLE,
    ANALYTICS_SEND_TO_TAGMANAGER,
    CMS_AVAILABLE_LOCALES,
    COOKIE_POLICY_URL,
    COMMUNITY_FORUM_URL,
    getAuthConfig, APPLICATION_INSIGHTS_CONNECTION_STRING,
} from '@common/app/app.config';
import { GoogleAnalyticsService } from '@common/services/google-analytics.service';
import { SeoService } from '@common/services/seo.service';
import { AuthService } from '@common/services/auth.service';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { RegistrationService } from './services/registration.service';
import localeENUS from '@angular/common/locales/en';
import { KenalyticsService } from '@common/services/kenalytics.service';
import { InMemoryQueryService } from '@common/services/inmemory.query.service';
import { WINDOW_PROVIDERS } from '@common/services/window.service';
import { EnusSeoService } from './services/enus-seo.service';
import { AppRoutingModule } from './app-routing.module';
import { InMemoryAuthStorage } from '@common/services/in-memory-auth-storage';
import { ParticipantStatusGuard } from './guards/participant-status.guard';
import { AuthGuard } from './guards/auth.guard';
import { ErrorHandlerService } from './services/error-handler.service';
import { MonitoringService } from '@common/services/monitoring.service';
import { AnalyticsEnricherProvider } from '@common/analytics/services/analytics-enricher.service';
import { LanguageService } from '@common/services/language.service';
import { AppConfig } from "@enus/model/app-config";
import { AnalyticsSolver } from './solvers/analytics.solver';
import { AnalyticsResolverProvider } from '@common/analytics/resolvers/analytics.resolver';
import {
    AppConfigService,
    APP_CONFIG,
} from "@enus/services/app-config.service";
import { PendingChangesGuard } from '@common/infrastructure/pending-changes.guard';
import { AddressesLookupService } from './services/address-lookup.service';
import { NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { NgbDateUSParserFormatter } from './utils/ngb-date-us-parser-formatter';
import { CommunityService } from '@common/services/community.service';
import { EnUsCommunityService } from './services/enus-community.service';
import { VwoService } from '@common/services/vwo.service';
import { CmsInfoItemsProvider } from '@common/components/info/cms-info-items-provider';
import { SharedModule } from './shared.module';
import { AuctionService } from './services/auction.service';
import { RegisterFlowLocationAnalyticsPageOptions } from '@common/services/register-flow-location-analytics-page.service';
import { RegisterFlowCalculatorBase } from '@common/services/register-flow-calculator.service.base';
import { RegisterFlowCalculator } from './services/register-flow-calculator.service';
import { RegisterFlowActionTracker } from './services/register-flow-action-tracker.service';
import { VwoStoreService } from '@common/services/vwo-store.service';
import { ProcessStepService } from './services/process-step.service';
import { ProcessGoalEventFactory } from '@common/analytics/services/process-goal-event-factory.service';
import { EnusProcessGoalEventFactory } from './services/process-goal-event-factory.service';
import { RedirectGuard } from './guards/redirect.guard';
import { ProposalContactDataService } from './services/proposal-contact-data.service';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { AppCommonModule } from '@common/app-common.module';
import { MenuComponent } from '@enus/components/menu/menu.component';
import { FooterComponent } from '@enus/components/footer/footer.component';
import { HomeProxyComponent } from '@enus/components/home/home-proxy/home-proxy.component';
import { HomeEnusComponent } from '@enus/components/home/home-enus/home-enus.component';
import { HeroImageComponent } from '@enus/components/home/hero-image/hero-image.component';
import { TilesInfoComponent } from '@enus/components/home/tiles-info/tiles-info.component';
import { HowItWorksComponent } from '@enus/components/home/how-it-works/how-it-works.component';
import { PartnerListComponent } from '@enus/components/home/partner-list/partner-list.component';
import { TestimonialsComponent } from '@enus/components/home/testimonials/testimonials.component';
import { InfoRegisterComponent } from '@enus/components/standalone/info-register/info-register.component';
import { RegisterBtnsComponent } from '@enus/components/standalone/register-btns/register-btns.component';
import { HomeSmeComponent } from '@enus/components/home/home-sme/home-sme.component';
import { SupplierService } from '@common/services/supplier.service';

registerLocaleData(localeENUS, 'en-US');

@NgModule({
    declarations: [
        AppComponent,
        MenuComponent,
        FooterComponent,
        HomeProxyComponent,
        HomeEnusComponent,
        HeroImageComponent,
        TilesInfoComponent,
        HowItWorksComponent,
        PartnerListComponent,
        TestimonialsComponent,
        HomeSmeComponent,
    ],
    providers: [
        WINDOW_PROVIDERS,
        {
            provide: AppConfigService,
            useFactory: (config: AppConfig) => new AppConfigService(config),
            deps: [APP_CONFIG],
        },
        {
            provide: OAuthModuleConfig,
            useFactory: (): OAuthModuleConfig => {
                return {
                    resourceServer: {
                        allowedUrls: [environment.apiUri],
                        sendAccessToken: true,
                    },
                };
            },
            deps: [AppConfigService],
        },
        {
            provide: AuthConfig,
            useFactory: () => getAuthConfig(environment.identityUri),
            deps: [AppConfigService],
        },
        RegistrationService,
        ContentService,
        GoogleAnalyticsService,
        AuthService,
        InMemoryQueryService,
        PendingChangesGuard,
        AuthGuard,
        ParticipantStatusGuard,
        RedirectGuard,
        AddressesLookupService,
        KenalyticsService,
        LanguageService,
        {
            provide: APPLICATION_INSIGHTS_CONNECTION_STRING,
            useFactory: () => environment.aiConnectionString,
            deps: [AppConfigService]
        },
        {
            provide: MonitoringService,
            deps: [BUSINESS, APPLICATION_INSIGHTS_CONNECTION_STRING]
        },
        CommunityService,
        EnUsCommunityService,
        VwoService,
        CmsInfoItemsProvider,
        AuctionService,
        ProposalContactDataService,
        RegisterFlowActionTracker,
        VwoStoreService,
        ProcessStepService,
        { provide: SeoService, useClass: EnusSeoService },
        { provide: OAuthStorage, useClass: InMemoryAuthStorage },
        { provide: ValidationHandler, useClass: JwksValidationHandler },
        SupplierService,
        AnalyticsSolver,
        {
            provide: ProcessGoalEventFactory,
            useClass: EnusProcessGoalEventFactory,
        },
        {
            provide: AnalyticsResolverProvider,
            deps: [RegistrationService, CommunityService, AnalyticsSolver],
        },
        {
            provide: AnalyticsEnricherProvider,
            deps: [RegistrationService, EnUsCommunityService],
        },
        {
            provide: APP_BASE_HREF,
            useFactory: getBaseHref,
            deps: [PlatformLocation],
        },
        { provide: NgbDateParserFormatter, useClass: NgbDateUSParserFormatter },
        { provide: LOCALE_ID, useValue: 'en-US' },
        { provide: BUSINESS, useValue: 'enus' },
        { provide: CMS_PRODUCT, useValue: 'Energy' },
        { provide: CMS_REGION, useValue: 'us' },
        { provide: CMS_LOCALE, useValue: 'en' },
        { provide: DEFAULT_COMMUNITY, useValue: 'texas' },
        { provide: CANONICAL_COMMUNITY, useValue: 'texas' },
        { provide: CMS_AVAILABLE_LOCALES, useValue: ['en', 'es'] },
        {
            provide: ANALYTICS_SEND_TO_TAGMANAGER,
            useFactory: () => true,
            deps: [AppConfigService],
        },
        {
            provide: API_URL,
            useFactory: () => environment.apiUri,
            deps: [AppConfigService],
        },
        {
            provide: COMMON_API_URL,
            useFactory: () => environment.addressServiceUri,
            deps: [AppConfigService],
        },
        {
            provide: CMS_URL,
            useFactory: () => environment.cmsUri,
            deps: [AppConfigService],
        },
        {
            provide: CMS_ALLOW_LINKS,
            useFactory: () => environment.allowLinksToCms,
            deps: [AppConfigService],
        },
        {
            provide: ANALYTICS_API_URL,
            useFactory: () => environment.analyticsUri,
            deps: [AppConfigService],
        },
        { provide: COOKIE_POLICY_URL, useValue: '' },
        { provide: ErrorHandler, useClass: ErrorHandlerService },
        {
            provide: COMMUNITY_FORUM_URL,
            useFactory: () => environment.communityForumUri,
            deps: [AppConfigService],
        },
        {
            provide: RegisterFlowLocationAnalyticsPageOptions,
            useValue: { includeUtmCodes: true },
        },
        {
            provide: RegisterFlowCalculatorBase,
            useClass: RegisterFlowCalculator,
        },
        {
            provide: ANALYTICS_LOG_TO_CONSOLE,
            useFactory: () => !environment.analyticsSendToTagManager,
            deps: [AppConfigService],
        },
    ],
    bootstrap: [AppComponent],
    imports: [
        SharedModule,
        NgbModule,
        BrowserModule,
        AppCommonModule,
        AppRoutingModule,
        InfoRegisterComponent,
        RegisterBtnsComponent,
        OAuthModule.forRoot(),
    ],
})
export class AppModule {}

export function getBaseHref(platformLocation: PlatformLocation): string {
    return platformLocation.getBaseHrefFromDOM();
}
